import { type JSONContent } from '@tiptap/react'

/**
 * Markdown ⇄ the block editor's document, in the browser, loaded on demand.
 *
 * The browser normally never serialises Markdown: the block editor posts its
 * document and the server converts it, which is why the Markdown tab, the block
 * editor and the MCP write byte-identical files. Help with the words (§22) needs
 * an exception in both directions — the selection has to be SENT as Markdown,
 * and the suggestion comes back as Markdown and has to become blocks — and the
 * bridge it uses is the same property-tested one the server uses, imported here
 * rather than reimplemented.
 *
 * Loaded when somebody first asks for help, and cached after that. A static
 * import would pull remark and mdast into the bundle every writer downloads for
 * a feature most of them will not use on any given page.
 */
type Bridge = typeof import('@staleless/markdown')

let loaded: Promise<Bridge> | null = null
const bridge = (): Promise<Bridge> => (loaded ??= import('@staleless/markdown'))

/**
 * What the writer selected, as Markdown they would recognise.
 *
 * The bridge speaks plain JSON in both directions — no ProseMirror instances at
 * the package boundary, which is what `convert.ts` says and why nothing here has
 * to build a schema to use it.
 */
export async function contentToMarkdown(content: JSONContent[]): Promise<string> {
  if (content.length === 0) return ''
  const { proseMirrorToMarkdown } = await bridge()
  return proseMirrorToMarkdown({}, { type: 'doc', content } as never).trimEnd()
}

/**
 * A suggestion, as blocks to put back.
 *
 * Nodes rather than a document: this replaces a range inside a page, so the
 * `doc` wrapper is dropped and its children are what gets inserted.
 */
export async function markdownToContent(markdown: string): Promise<JSONContent[]> {
  const { markdownToProseMirror } = await bridge()
  const { doc } = markdownToProseMirror(markdown)
  return (doc.content ?? []) as JSONContent[]
}
